import { useMemo } from 'react';
import {
    validateItemForApprove,
    validateItemsForBatchApprove,
    detectDuplicateFilenames,
} from '../../fileUpload/utils/validation';

/**
 * Client-side case / slide completeness from doc.items + validation helpers.
 */
export function useCaseCompleteness(caseDoc) {
    return useMemo(() => {
        const items = Array.isArray(caseDoc?.items) ? caseDoc.items : [];
        const quarantinedItems = items.filter((i) => i.girderNetwork?.quarantined === true);

        const itemStatuses = items.map((item) => {
            const validation = validateItemForApprove(item);
            return {
                item,
                isComplete: validation.isValid,
                errors: validation.errors,
                isQuarantined: item.girderNetwork?.quarantined === true,
            };
        });

        const completeCount = itemStatuses.filter((s) => s.isComplete).length;
        const incompleteItems = itemStatuses.filter((s) => !s.isComplete);
        const duplicates = detectDuplicateFilenames(caseDoc, items);
        const duplicateCount = [...duplicates.values()].reduce((sum, group) => sum + group.length, 0);

        const batchResults = quarantinedItems.length
            ? validateItemsForBatchApprove(caseDoc, quarantinedItems)
            : { validated: [], incomplete: [], duplicates: new Map() };

        const releaseBlocked = batchResults.incomplete.length + batchResults.duplicates.size;
        const releaseReady = batchResults.validated.length;

        const hasSlides = items.length > 0;
        const allSlidesComplete = hasSlides && incompleteItems.length === 0 && duplicates.size === 0;
        const hasClinical = !!(caseDoc?.schemaId || (caseDoc?.details && Object.keys(caseDoc.details).length > 0));
        const hasBlocks = Array.isArray(caseDoc?.blocks) && caseDoc.blocks.length > 0;
        const hasProtocol = !!caseDoc?.protocolId;

        let status = 'draft';
        if (hasCaseIdentity(caseDoc)) {
            if (caseDoc.quarantined && allSlidesComplete && releaseReady > 0) {
                status = 'ready';
            } else if (hasSlides || hasClinical || hasBlocks) {
                status = 'in_progress';
            }
        }

        return {
            items,
            itemStatuses,
            slideCount: items.length,
            completeCount,
            incompleteCount: incompleteItems.length,
            incompleteItems,
            duplicateCount,
            duplicates,
            quarantinedCount: quarantinedItems.length,
            releaseBlocked,
            releaseReady,
            batchResults,
            hasSlides,
            allSlidesComplete,
            hasClinical,
            hasBlocks,
            hasProtocol,
            status,
            completenessPercent: items.length ? Math.round((completeCount / items.length) * 100) : 0,
        };
    }, [caseDoc]);
}

function hasCaseIdentity(caseDoc) {
    return !!(caseDoc?._id || caseDoc?.networkIdentifier);
}
